import { filter, debounceTime, switchMap, takeUntil, skip } from 'rxjs';
import { IContext } from '@context/models/context';
import { Observable, OperatorFunction } from 'rxjs';

export interface EntityKeyResponse {
   Key: EntityKey;
}

export interface HasEntityKey {
  entityKey: Observable<EntityKey>;
}
export abstract class EntityKey {
  abstract EntityTypeName: string;
  abstract setByContext(context: IContext): void;
}

// should map to SupplierKeyDto in domain
export class SupplierKey implements EntityKey {
  EntityTypeName = 'Supplier';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.SupplierRef;
  }
}

// should map to CreditorKeyDto in domain
export class CreditorKey implements EntityKey {
  EntityTypeName = 'Creditor';
  Uri: number;
  setByContext(context: IContext): void {
    this.Uri = context.CreditorUri;
  }
}

export class DebtorKey implements EntityKey {
  EntityTypeName = 'Debtor';
  Uri: number;
  setByContext(context: IContext): void {
    this.Uri = context.DebtorUri;
  }
}

export class PropertyKey implements EntityKey {
  EntityTypeName = 'Property';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.PropertyRef;
  }
}

export class LeaseKey implements EntityKey {
  EntityTypeName = 'Lease';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.LeaseRef;
  }
}

export class EstateKey implements EntityKey {
  EntityTypeName = 'Estate';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.EstateRef;
  }
}

export class ClientKey implements EntityKey {
  EntityTypeName = 'Client';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.UserEnteredClientRef;
  }
}

export class ClientParentKey implements EntityKey {
  EntityTypeName = 'ClientParent';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.UserEnteredClientParentRef;
  }
}

export class ManagerKey implements EntityKey {
  EntityTypeName = 'Manager';
  Code: string;
  setByContext(context: IContext): void {
    this.Code = context.ManagerCode;
  }
}

export class TenantKey implements EntityKey {
  EntityTypeName = 'Tenant';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.TenantRef;
  }
}

export class LandlordKey implements EntityKey {
  EntityTypeName = 'Landlord';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.LandlordRef;
  }
}

export class TenantParentKey implements EntityKey {
  EntityTypeName = 'TenantParent';
  Ref: number;
  setByContext(context: IContext): void {
    this.Ref = context.TenantParentRef;
  }
}

export class UnitKey implements EntityKey {
  EntityTypeName = 'Unit';
  UnitRef: number;
  PropertyRef: number;
  setByContext(context: IContext): void {
    this.UnitRef = context.UnitRef;
    this.PropertyRef = context.PropertyRef;
  }
}

export class NotificationRequestKey implements EntityKey {
  EntityTypeName = 'NotificationRequest';
  Uri: number;
  // currently only used in a dialog not from context
  setByContext(context: IContext): void {
    throw new Error('Method not implemented.');
  }
}

export class HeadLeaseKey implements EntityKey {
  EntityTypeName = 'HeadLease';
  HeadLeaseRef: number;
  PropertyRef: number;
  setByContext(context: IContext): void {
    this.HeadLeaseRef = context.HeadLeaseRef;
    this.PropertyRef = context.PropertyRef;
  }
}

export class OccupantKey implements EntityKey {
  EntityTypeName = 'Occupant';
  Uri: number;
  setByContext(context: IContext): void {
    this.Uri = context.OccupantUri;
  }
}

class EmptyEntityKey implements EntityKey {
  EntityTypeName = 'Empty Entity Key';
  setByContext(context: IContext): void {
    return;
  }
}

export const emptyEntityKey = new EmptyEntityKey();

export const isEmptyEntityKey = (k: EntityKey) => k.EntityTypeName === emptyEntityKey.EntityTypeName;

export const filterOutEmptyKeys = () => filter((k: EntityKey) => !isEmptyEntityKey(k));

export interface EntityLookupItemDto {
  Key: any;
  Name: string;
}

/**
 * A standard autocomplete/typeahead function that may be used to transform a stream of user-input "query" (typically a string)
 * into an observable collection of "suggestion" results.
 * 
 * @param time The time (in milliseconds) to debounce the input.  See https://rxjs.dev/api/operators/debounceTime dueTime parameter
 * @param selector A selector/mapping function that gets an observable of suggestion results (such as from an API) based upon the source
 * @returns An RXJS operator function which may be used in a pipe operation.  See https://rxjs.dev/guide/operators
 */
const autocomplete = function<TSource,TDestination>(time : number, selector : (src : TSource) => Observable<TDestination>) : OperatorFunction<TSource,TDestination> {
    return source => source.pipe(
        debounceTime(time),
        switchMap(src => selector(src).pipe(
            takeUntil(
                source.pipe(skip(1))
            )
        ))
    );
}

export { autocomplete };
